import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { handleEnterPress } from '../../shared/helpers/util';
import Call from '../components/Call';
import { answerCall, endCall, holdCallAndDeactivate } from '../reducers/calls';
import { StoreDispatch, StoreState } from '../store';
import * as styles from '../styles/callList.module.scss';
import { HangUpIcon, NewCallIcon, PhoneIcon } from './icons';

const CallList: React.FC = () => {
  const dispatch = useDispatch<StoreDispatch>();
  const calls = useSelector((state: StoreState) => state.calls || []);
  const [containerVisible, setContainerVisible] = useState(false);

  // Arming a transfer on a consultation (outbound) leg reveals the inbound
  // target buttons here, so auto-expand on that transition — but only once, so
  // the user can still collapse the pane (e.g. to cancel the transfer).
  const awaitingTransferTarget = calls.some(
    (c) => c.transferInitiated && c.direction === 'OUT',
  );
  const [prevAwaitingTarget, setPrevAwaitingTarget] = useState(
    awaitingTransferTarget,
  );
  if (awaitingTransferTarget !== prevAwaitingTarget) {
    setPrevAwaitingTarget(awaitingTransferTarget);
    if (awaitingTransferTarget) {
      setContainerVisible(true);
    }
  }

  const activeCall = calls.find((c) => c.active);

  const ongoingCallCount = calls.length;

  let title = `${ongoingCallCount} Ongoing ${
    ongoingCallCount === 1 ? 'Call' : 'Calls'
  }`;

  const incomingCall = calls.find((c) => c.direction === 'IN' && c.ringing);

  if (incomingCall) {
    title = `Incoming call from ${incomingCall.number}`;
  }

  const containerBaseClass = ongoingCallCount
    ? styles.container
    : styles.container_empty;

  const containerVisibleClass =
    containerVisible && ongoingCallCount
      ? styles.container_open
      : styles.container_closed;

  const containerStatusClass =
    incomingCall && !containerVisible ? styles.container_active : '';

  const containerClass = `
      ${containerBaseClass}
      ${containerVisibleClass}
      ${containerStatusClass}
    `;

  let quickActions: JSX.Element | null = null;

  if (activeCall) {
    if (incomingCall) {
      quickActions = (
        <span className={styles.clickable}>
          <span
            className={styles.icon_container_green}
            onClick={() => {
              dispatch(answerCall(incomingCall.id));
            }}
            onKeyDown={handleEnterPress(() => {
              dispatch(answerCall(incomingCall.id));
            })}
            role='button'
            tabIndex={0}
          >
            <PhoneIcon />
          </span>
          <span
            className={styles.icon_container_red}
            onClick={() => {
              dispatch(endCall(incomingCall.id));
            }}
            onKeyDown={handleEnterPress(() => {
              dispatch(endCall(incomingCall.id));
            })}
            role='button'
            tabIndex={0}
          >
            <HangUpIcon />
          </span>
        </span>
      );
    } else {
      quickActions = (
        <span className={styles.clickable}>
          <button
            className={styles.icon_container}
            onClick={() => {
              setContainerVisible(false);
              dispatch(holdCallAndDeactivate(activeCall?.id));
            }}
          >
            <NewCallIcon />
          </button>
        </span>
      );
    }
  }

  return (
    <div className={containerClass}>
      {ongoingCallCount ? (
        <div className={styles.call_summary}>
          <span
            className={styles.call_summary_title}
            onClick={() => {
              setContainerVisible(!containerVisible);
            }}
            onKeyDown={handleEnterPress(() => {
              setContainerVisible(!containerVisible);
            })}
            role='button'
            tabIndex={0}
          >
            {title}
          </span>

          {quickActions}
        </div>
      ) : (
        ''
      )}

      {ongoingCallCount ? (
        // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
        <ul
          className={styles.list}
          onClick={() => {
            setContainerVisible(false);
          }}
          onKeyDown={handleEnterPress(() => {
            setContainerVisible(false);
          })}
        >
          {calls.map((call, i) => {
            return <Call key={i} id={call.id} />;
          })}
        </ul>
      ) : (
        <span className={styles.empty_message}>No ongoing calls</span>
      )}
    </div>
  );
};

export default CallList;
